import {useGetCurrentUserQuery} from '../services/userService'
import {useAppSelector} from './store'
import {isBarberLoggedIn} from '../slices/authSlice'
import type {CurrentUser} from '../types'
import {Role} from '../types'

// Centralised access to the /me response. Skips the request when no
// barber token is set so unauthenticated pages don't trigger 401s.
export const useCurrentUser = (): CurrentUser | undefined => {
    const isLogged = useAppSelector(isBarberLoggedIn)
    const {data} = useGetCurrentUserQuery(undefined, {skip: !isLogged})
    return data
}

// True when the user is the shop owner. Used to gate owner-only Settings
// rows and the Staff section. Derived from roles — there is no `is_owner`
// column on the BE.
export const useIsOwner = (): boolean => {
    const user = useCurrentUser()
    return user?.roles?.includes(Role.Owner) ?? false
}

// Owner always can. Non-owner barbers need the owner-granted flag
// `can_services` (FE #30 / BE #27).
export const useCanEditServices = (): boolean => {
    const user = useCurrentUser()
    if (!user) return false
    if (user.roles.includes(Role.Owner)) return true
    return !!user.can_services
}

// Owner always can. Non-owner barbers need the owner-granted flag
// `can_hours`.
export const useCanEditHours = (): boolean => {
    const user = useCurrentUser()
    if (!user) return false
    if (user.roles.includes(Role.Owner)) return true
    return !!user.can_hours
}

// True when the shop hasn't completed onboarding yet. The route guard
// (FE #24) uses this to redirect new owners to /onboarding.
export const useNeedsOnboarding = (): boolean => {
    const user = useCurrentUser()
    if (!user) return false
    return !user.barber_shop.onboarded
}
